import { useEffect, useState } from 'react'
import { AlertCircle, Check, Loader2 } from 'lucide-react'

export type SaveState = 'idle' | 'saving' | 'saved' | 'error'

export interface SaveIndicatorProps {
  state: SaveState
}

export function SaveIndicator({ state }: SaveIndicatorProps) {
  // The only stateful behavior is auto-hiding the 'saved' message after 2s.
  // Everything else is derived directly from `state`, so no state-syncing
  // effect is needed.
  const [savedHidden, setSavedHidden] = useState(false)
  const [prevState, setPrevState] = useState(state)

  // Reset the auto-hide flag whenever `state` changes (React's
  // adjust-state-during-render pattern — avoids a setState-in-effect).
  if (state !== prevState) {
    setPrevState(state)
    setSavedHidden(false)
  }

  useEffect(() => {
    if (state !== 'saved') return
    const timer = setTimeout(() => setSavedHidden(true), 2000)
    return () => clearTimeout(timer)
  }, [state])

  if (state === 'idle') return null
  if (state === 'saved' && savedHidden) return null

  const containerStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-1)',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-label-sm)',
    color: 'var(--color-text-secondary)',
    lineHeight: 'var(--leading-normal)',
    transition: 'opacity 250ms ease-out',
    opacity: 1,
  }

  if (state === 'saving') {
    return (
      <span style={containerStyle} aria-live="polite" aria-label="Saving...">
        <Loader2
          size={12}
          aria-hidden="true"
          style={{ animation: 'spin 1s linear infinite' }}
        />
        Saving...
      </span>
    )
  }

  if (state === 'saved') {
    return (
      <span style={containerStyle} aria-live="polite" aria-label="Saved">
        <Check size={12} aria-hidden="true" />
        Saved
      </span>
    )
  }

  if (state === 'error') {
    return (
      <span
        style={{ ...containerStyle, color: 'var(--color-error-text)' }}
        aria-live="assertive"
        aria-label="Error saving"
      >
        <AlertCircle size={12} aria-hidden="true" />
        Error saving
      </span>
    )
  }

  return null
}
